/**
 * Patient List Component
 */

import React, { useState, useEffect } from 'react';
import { patientsService } from '../../services/api';
import './PatientList.css';
import { Ico, emo } from '../ui/Ico';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://levnutri.com/nutri';

function ReturnIndicator({ days }) {
  let color, label;
  if (days === null || days === undefined) {
    color = '#a39b93';    label = 'Nunca consultou';
  } else if (days <= 30) {
    color = '#46714f'; label = days === 0 ? 'Consultou hoje' : `${days}d atrás`;
  } else if (days <= 90) {
    color = '#c4933f'; label = `${days} dias sem consulta`;
  } else {
    color = '#c46a60'; label = `${days} dias sem consulta`;
  }
  return (
    <span title={label} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.72rem', color }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
      {emo(label)}
    </span>
  );
}

function PatientList({ onNavigate }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const response = await patientsService.getAll('ativo');
      if (response.success && response.data) {
        setPatients(response.data);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Carregando pacientes...</div>;
  }

  return (
    <div className="patient-list-container">
      <header className="list-header">
        <div>
          <h1>Pacientes</h1>
          <p className="subtitle">{patients.length} pacientes cadastrados</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => onNavigate('patient-new')}
        >
          <Ico e="➕" sp />Novo Paciente
        </button>
      </header>

      <div className="search-bar">
        <span className="search-icon"><Ico e="🔍" /></span>
        <input
          type="text"
          placeholder="Buscar paciente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="patients-grid">
        {filteredPatients.map(patient => (
          <div 
            key={patient.id}
            className="patient-card"
            onClick={() => onNavigate('patient-detail', patient.id)}
          >
            <div className="patient-header">
              <div className="patient-avatar-large" style={{ overflow: 'hidden', padding: 0 }}>
                {patient.foto_url
                  ? <img src={`${API_BASE}${patient.foto_url}`} alt={patient.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : patient.name.charAt(0)
                }
              </div>
              <div className="patient-basic-info">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {patient.name}
                </h3>
                <p>{patient.age} anos • {patient.gender === 'F' ? 'Feminino' : 'Masculino'}</p>
                <ReturnIndicator days={patient.days_since_last_appt != null ? parseInt(patient.days_since_last_appt, 10) : null} />
              </div>
            </div>

            <div className="patient-objective">
              <span className="objective-badge">
                {emo(patient.objective === 'perda_peso' && '⬇️ Perda de Peso')}
                {emo(patient.objective === 'ganho_peso' && '⬆️ Ganho de Peso')}
                {emo(patient.objective === 'manutencao' && '➡️ Manutenção')}
                {emo(patient.objective === 'ganho_massa' && '💪 Ganho de Massa')}
              </span>
            </div>

            {patient.last_weight && (
              <div className="patient-stats">
                <div className="stat-item">
                  <span className="stat-label">Peso</span>
                  <span className="stat-value">{patient.last_weight}kg</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">IMC</span>
                  <span className="stat-value">{patient.last_imc}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Gordura</span>
                  <span className="stat-value">{patient.last_body_fat}%</span>
                </div>
              </div>
            )}

            <div className="card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="view-details">Ver detalhes →</span>
              {patient.whatsapp && (
                <a
                  href={`https://wa.me/55${patient.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: '1.1rem', lineHeight: 1, textDecoration: 'none' }}
                  title="Abrir no WhatsApp"
                >
                  <Ico e="💬" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredPatients.length === 0 && (
        <div className="empty-state">
          <span className="empty-icon"><Ico e="👥" /></span>
          <h3>Nenhum paciente encontrado</h3>
          <p>Cadastre seu primeiro paciente para começar</p>
          <button 
            className="btn btn-primary"
            onClick={() => onNavigate('patient-new')}
          >
            <Ico e="➕" sp />Cadastrar Paciente
          </button>
        </div>
      )}
    </div>
  );
}

export default PatientList;
